import { z } from 'zod';

import { ADJUST_KINDS, projectedOnHand } from './inventory-rules';

const WHOLE = /^[+-]?\d+$/;
const MAX_QUANTITY = 1_000_000;

/**
 * adjust is a signed delta, receive a positive delivery, count the absolute physical quantity.
 * The current on-hand lets the form refuse what the backend would answer 409 to.
 */
export function adjustStockSchema(onHand: number) {
  return z
    .object({
      kind: z.enum(ADJUST_KINDS),
      // Output (a number) must stay assignable to the input for react-hook-form's Control type.
      quantity: z.union([z.string(), z.number()]).transform((v) => String(v).trim()),
      reason: z
        .string()
        .trim()
        .min(1, { error: 'inventory:adjust.validation.reasonRequired' })
        .max(500, { error: 'inventory:adjust.validation.reasonLength' }),
    })
    .transform((v, ctx) => {
      const fail = (message: string) => {
        ctx.addIssue({ code: 'custom', path: ['quantity'], message });
        return z.NEVER;
      };
      if (!WHOLE.test(v.quantity)) return fail('inventory:adjust.validation.quantityWhole');
      const quantity = Number(v.quantity);
      if (Math.abs(quantity) > MAX_QUANTITY)
        return fail('inventory:adjust.validation.quantityRange');
      if (v.kind === 'receive' && quantity <= 0) {
        return fail('inventory:adjust.validation.receivePositive');
      }
      if (v.kind === 'count' && quantity < 0) {
        return fail('inventory:adjust.validation.countNonNegative');
      }
      if (v.kind === 'adjust' && quantity === 0) return fail('inventory:adjust.validation.nonZero');
      if (projectedOnHand(onHand, v.kind, quantity) < 0) {
        return fail('inventory:adjust.validation.belowZero');
      }
      return { kind: v.kind, quantity, reason: v.reason };
    });
}

export type AdjustStockFormInput = z.input<ReturnType<typeof adjustStockSchema>>;
export type AdjustStockFormOutput = z.output<ReturnType<typeof adjustStockSchema>>;
